import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { fmtMoney } from "@/lib/money";
import { DollarSign, TrendingUp, TrendingDown, Users, Truck, FolderKanban, Receipt, Calendar, AlertTriangle, ArrowRight, Percent } from "lucide-react";
import { Link } from "react-router-dom";

const STAGE_COLORS = {
  ordered: "#94A3B8", in_process: "#3B82F6", awaiting_arrival: "#8B5CF6",
  scheduling: "#F59E0B", scheduled: "#F97316", installed: "#0EA5E9", completed: "#10B981",
};

export default function AdminExecutive() {
  const [data, setData] = useState(null);
  const [pnl, setPnl] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        const [e, p] = await Promise.all([
          api.get("/admin/executive"),
          api.get(`/finance/pnl?from_month=${monthKey(start)}&to_month=${monthKey(now)}`),
        ]);
        setData(e.data);
        setPnl(p.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Load failed");
      }
    })();
  }, []);

  if (!data) return <div className="text-sm text-[#94A3B8]">Loading cockpit…</div>;

  const stages = Object.entries(data.project_stages || {});
  const totalProj = stages.reduce((s, [, c]) => s + c, 0) || 1;

  return (
    <div data-testid="admin-executive" className="space-y-6">
      <div>
        <p className="overline">Owner's Cockpit</p>
        <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
          Executive Dashboard
        </h1>
        <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
          Live financial + operational health · as of {new Date(data.as_of).toLocaleString()}
        </p>
      </div>

      {/* Top KPI row — Cash / Revenue / Net / AR / AP / Wages */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Cash on hand" value={fmtMoney(data.cash_on_hand)} icon={DollarSign} tone="good" testid="kpi-cash" hint="Book balance" />
        <Kpi label="Revenue MTD"  value={fmtMoney(data.mtd_revenue)} icon={TrendingUp} tone="good" testid="kpi-rev" />
        <Kpi label="Net MTD"      value={fmtMoney(data.mtd_net)}     icon={TrendingUp} tone={data.mtd_net >= 0 ? "good" : "bad"} testid="kpi-net" />
        <Kpi label="AR outstanding" value={fmtMoney(data.ar_open)} icon={ArrowRight} testid="kpi-ar" hint="Unpaid invoices" to="/admin/payments" />
        <Kpi label="AP owed"      value={fmtMoney(data.ap_open)}   icon={Truck} tone={data.ap_open > 0 ? "warn" : "muted"} testid="kpi-ap" to="/admin/purchase-orders" />
        <Kpi label="Wages payable" value={fmtMoney(data.wages_payable)} icon={Calendar} tone={data.wages_payable > 0 ? "warn" : "muted"} testid="kpi-wages" to="/admin/timesheets" />
      </div>

      {/* MTD P&L */}
      <div className="card-pb p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="overline">Month-to-date</p>
            <h2 className="font-heading text-lg text-[#0A2540] dark:text-white mt-1">P&amp;L snapshot</h2>
          </div>
          <Link to="/admin/pnl" className="text-xs text-[#C9A356] hover:underline">Full P&amp;L →</Link>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <MiniStat label="Revenue MTD" value={data.mtd_revenue} tone="good" />
          <MiniStat label="Expense MTD" value={data.mtd_expense} tone="bad" />
          <MiniStat label="Net MTD" value={data.mtd_net} tone={data.mtd_net >= 0 ? "good" : "bad"} bold />
        </div>
      </div>

      {/* Two-column below */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Project stages */}
        <div className="card-pb p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-1.5">
              <FolderKanban className="w-4 h-4 text-[#C9A356]" />
              <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">Projects by stage</h2>
            </div>
            <Link to="/admin/projects" className="text-xs text-[#C9A356] hover:underline">Open →</Link>
          </div>
          <div className="flex h-3 rounded overflow-hidden mb-3">
            {stages.map(([stage, count]) => (
              <div key={stage} title={`${stage}: ${count}`}
                   style={{ width: `${(count / totalProj) * 100}%`, background: STAGE_COLORS[stage] || "#94A3B8" }} />
            ))}
          </div>
          <ul className="space-y-1 text-xs">
            {stages.map(([stage, count]) => (
              <li key={stage} data-testid={`stage-count-${stage}`}>
                <Link
                  to={`/admin/projects?stage=${stage}`}
                  className="flex items-center justify-between py-0.5 rounded hover:bg-slate-50 dark:hover:bg-slate-800/60 transition px-1"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: STAGE_COLORS[stage] || "#94A3B8" }} />
                    <span className="capitalize text-[#475569] dark:text-slate-400">{stage.replace(/_/g, " ")}</span>
                  </span>
                  <span className="tabular-nums font-medium">{count}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* AR Aging */}
        <div className="card-pb p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">AR Aging</h2>
            </div>
            <Link to="/admin/payments" className="text-xs text-[#C9A356] hover:underline">Invoices →</Link>
          </div>
          <ul className="space-y-1.5 text-sm">
            {[["0-30", "good"], ["31-60", "warn"], ["61-90", "warn"], ["90+", "bad"]].map(([bucket, tone]) => {
              const amt = data.ar_aging_totals[bucket] || 0;
              const pct = data.ar_aging_totals.grand > 0 ? (amt / data.ar_aging_totals.grand) * 100 : 0;
              return (
                <li key={bucket} data-testid={`ar-bucket-${bucket}`}>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className={tone === "bad" ? "text-red-500 font-medium" : tone === "warn" ? "text-amber-600" : "text-[#475569] dark:text-slate-400"}>
                      {bucket} days
                    </span>
                    <span className="tabular-nums">{fmtMoney(amt)}</span>
                  </div>
                  <div className="h-1.5 bg-[#F8FAFC] dark:bg-slate-800 rounded overflow-hidden">
                    <div className={`h-full ${tone === "bad" ? "bg-red-500" : tone === "warn" ? "bg-amber-500" : "bg-emerald-500"}`}
                         style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="text-right mt-3 pt-3 border-t border-[#E2E8F0] dark:border-slate-700 text-xs">
            Total outstanding: <b className="text-[#0A2540] dark:text-white">${data.ar_aging_totals.grand.toLocaleString()}</b>
          </div>
        </div>

        {/* Top suppliers */}
        <div className="card-pb p-5">
          <div className="inline-flex items-center gap-1.5 mb-3">
            <Truck className="w-4 h-4 text-[#C9A356]" />
            <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">Top suppliers · YTD</h2>
          </div>
          {(data.top_suppliers_ytd || []).length === 0 ? (
            <p className="text-xs text-[#94A3B8] italic">No supplier spend recorded yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.top_suppliers_ytd.map((s, i) => (
                <li key={i} className="flex items-center justify-between">
                  <span className="text-[#0A2540] dark:text-white">{i + 1}. {s.supplier}</span>
                  <span className="tabular-nums font-medium">${s.total.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Upcoming payroll */}
        <div className="card-pb p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-1.5">
              <Users className="w-4 h-4 text-[#C9A356]" />
              <h2 className="font-heading text-lg text-[#0A2540] dark:text-white">Upcoming payroll</h2>
            </div>
            <Link to="/admin/timesheets" className="text-xs text-[#C9A356] hover:underline">Timesheets →</Link>
          </div>
          {(data.upcoming_payroll || []).length === 0 ? (
            <p className="text-xs text-[#94A3B8] italic">No approved hours pending payment.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.upcoming_payroll.map((p, i) => (
                <li key={i} className="flex items-center justify-between" data-testid={`payroll-${i}`}>
                  <span className="text-[#0A2540] dark:text-white">{p.employee}</span>
                  <span className="text-xs text-[#94A3B8]">
                    {p.hours}h → <b className="text-[#0A2540] dark:text-white tabular-nums">{fmtMoney(p.amount)}</b>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, icon: Icon, tone, testid, to }) {
  const border = tone === "good" ? "border-emerald-500" : tone === "warn" ? "border-amber-500" : tone === "bad" ? "border-red-500" : "border-[#C9A356]";
  const inner = (
    <div className={`card-pb p-4 border-l-4 ${border} ${to ? "hover:-translate-y-0.5 transition cursor-pointer" : ""}`} data-testid={testid}>
      <div className="inline-flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-[#C9A356]" />
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">{label}</div>
      </div>
      <div className="text-xl md:text-2xl font-heading mt-2 text-[#0A2540] dark:text-white">{value}</div>
      {hint && <div className="text-[10px] text-[#94A3B8] mt-0.5">{hint}</div>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}
function MiniStat({ label, value, tone, bold }) {
  const color = tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-red-500" : "text-[#0A2540] dark:text-white";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">{label}</div>
      <div className={`${bold ? "text-2xl font-heading" : "text-xl"} mt-1 tabular-nums ${color}`}>
        {fmtMoney(value)}
      </div>
    </div>
  );
}
